import React from 'react';

// Design System
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import { ChevronLeft, ChevronRight } from 'Components/DesignSystem/Iconography/Icons';

export type EdgeCollapseSide = 'left' | 'right';

export interface EdgeCollapseToggleProps {
  side: EdgeCollapseSide;
  /** Whether the panel this control governs is currently collapsed. */
  collapsed: boolean;
  /** Accessible name — what the control toggles ("Collapse the properties panel"). */
  label: string;
  /** Offset class while the panel is expanded (the panel's width is the caller's). */
  expandedOffsetClass: string;
  onToggle: () => void;
  id?: string;
}

/**
 * EdgeCollapseToggle
 *
 * The chevron that collapses and expands a side column of the three-column editor. Mounted
 * at the column's outer edge, above the columns, and it STAYS on screen while the column is
 * collapsed — a control that disappears with the panel it closed cannot reopen it. It knows
 * nothing about the panel: the caller owns the state, the accessible name and the offset.
 *
 * On the design system since 2026-09-09: a DS `IconButton` (`aria-expanded` + `aria-label`,
 * the DS focus ring) with the DS chevrons — the two `<img>` glyphs are gone. Below `md` the
 * columns stack, so there is nothing to reclaim and the control is not drawn.
 *
 * ## 24px, below the control ladder (2026-09-29)
 *
 * This is chrome in a gutter, not a control in a form row, and at the ladder's `sm` (28px)
 * it read as heavy beside the thin column edge it sits on. So the box is overridden to
 * 24px and the chevron to 14px.
 *
 * **24 is the floor, not a preference.** WCAG SC 2.5.8 asks for a 24x24 CSS-pixel pointer
 * target, which this meets exactly; the DS ladder's own note explains that `sm` = 28 was
 * chosen to sit *above* that minimum. Anything smaller here would have to buy the room back
 * with spacing exclusions, and there is none to spare at a column edge.
 *
 * The override is `!tw-` because it has to be. Tailwind emits `.tw-h-6` BEFORE `.tw-h-7`,
 * so a plain `tw-h-6` passed through `className` loses to the component's own class and
 * changes nothing — the silent no-op this repo's jsdom note warns about, invisible to a
 * test that only reads `classList`. `!tw-py-0` / `!tw-px-0` elsewhere in the repo are the
 * same manoeuvre on the same kind of control.
 */
const collapsedOffsetClass: Record<EdgeCollapseSide, string> = {
  left: 'tw-left-0',
  right: 'tw-right-0',
};

const EdgeCollapseToggle = ({ side, collapsed, label, expandedOffsetClass, onToggle, id }: EdgeCollapseToggleProps) => {
  // The chevron points away from the panel while it is open (press me to close) and back
  // towards it while it is closed (press me to open), mirrored per side.
  const pointsLeft = (side === 'left') !== collapsed;

  return (
    <IconButton
      id={id}
      variant="secondary"
      size="sm"
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-label={label}
      title={label}
      className={`tw-absolute tw-top-1 tw-z-20 tw-hidden md:tw-inline-flex tw-bg-surface !tw-h-6 !tw-w-6 ${
        collapsed ? collapsedOffsetClass[side] : expandedOffsetClass
      }`}
    >
      {pointsLeft ? <ChevronLeft size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
    </IconButton>
  );
};

export default EdgeCollapseToggle;
